import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Shield, Loader2, Eye, EyeOff, User } from 'lucide-react';
import { toast } from 'sonner';
import { sessionStorage } from '../../utils/auth-api';
import { API_BASE } from '../../utils/api-base';

// ── All configurable admin panel tabs ──────────────────────────────────────────
const ADMIN_TABS = [
  { id: 'clients',           label: 'Clients',           group: 'Core' },
  { id: 'plcs',              label: 'PLCs',              group: 'Core' },
  { id: 'energy-meters',     label: 'Energy Meters',     group: 'Core' },
  { id: 'vfd-drives',        label: 'VFD Drives',        group: 'Core' },
  { id: 'controllers',       label: 'Controller Connection', group: 'Core' },
  { id: 'io-modules',        label: 'IO Modules (Waveshare)', group: 'Core' },
  { id: 'scanners',          label: 'Scanners',          group: 'Core' },
  { id: 'widgets',           label: 'Widgets',           group: 'Core' },
  { id: 'hmi-builder',       label: 'HMI Builder',       group: 'Core' },
  { id: 'settings',          label: 'Settings',          group: 'Config' },
  { id: 'advanced-pdf',      label: 'Advanced PDF',      group: 'Config' },
  { id: 'bridge-registry',   label: 'Bridge Registry',   group: 'Config' },
  { id: 'tickets',           label: 'Support Tickets',   group: 'Reports' },
  { id: 'audit-logs',        label: 'Audit Logs',        group: 'Reports' },
  { id: 'usage-analytics',   label: 'Usage Analytics',   group: 'Reports' },
  { id: 'system-logs',       label: 'System Logs',       group: 'Reports' },
  { id: 'storage-analytics', label: 'Storage Analytics', group: 'Reports' },
  { id: 'batch-reports',     label: 'Batch Reports',     group: 'Reports' },
];

const GROUPS = ['Core', 'Config', 'Reports'];

interface StaffUser {
  id: string;
  email: string;
  name: string;
  permissions: { adminTabs?: string[] };
  createdAt?: string;
}

interface FormState {
  email: string;
  name: string;
  password: string;
  adminTabs: string[];
}

const emptyForm = (): FormState => ({
  email: '',
  name: '',
  password: '',
  adminTabs: ['clients'],
});

export function AdminUsersTab() {
  const [users, setUsers]             = useState<StaffUser[]>([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState<StaffUser | null>(null);
  const [form, setForm]               = useState<FormState>(emptyForm());
  const [saving, setSaving]           = useState(false);
  const [showPwd, setShowPwd]         = useState(false);

  const token = sessionStorage.get()?.token || '';
  const authH = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = () => {
    setLoading(true);
    fetch(`${API_BASE}/api/admin/staff-users`, { headers: authH })
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => toast.error('Failed to load admin users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleTab = (tabId: string) => {
    setForm(f => ({
      ...f,
      adminTabs: f.adminTabs.includes(tabId)
        ? f.adminTabs.filter(t => t !== tabId)
        : [...f.adminTabs, tabId],
    }));
  };

  const selectAll = () => setForm(f => ({ ...f, adminTabs: ADMIN_TABS.map(t => t.id) }));
  const selectNone = () => setForm(f => ({ ...f, adminTabs: [] }));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowPwd(false);
    setShowForm(true);
  };

  const openEdit = (u: StaffUser) => {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name,
      password: '',
      adminTabs: u.permissions?.adminTabs || [],
    });
    setShowPwd(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    if (!editing && !form.password) { toast.error('Password is required for new users'); return; }
    setSaving(true);
    try {
      const body = {
        email:       form.email.trim(),
        name:        form.name.trim() || form.email.trim(),
        permissions: { adminTabs: form.adminTabs },
        ...(form.password ? { password: form.password } : {}),
      };

      const url    = editing ? `${API_BASE}/api/admin/staff-users/${editing.id}` : `${API_BASE}/api/admin/staff-users`;
      const method = editing ? 'PUT' : 'POST';
      const r      = await fetch(url, { method, headers: authH, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).error || 'Request failed');

      toast.success(editing ? 'User updated' : 'Admin user created');
      closeForm();
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u: StaffUser) => {
    if (!confirm(`Delete admin user "${u.email}"?`)) return;
    try {
      const r = await fetch(`${API_BASE}/api/admin/staff-users/${u.id}`, { method: 'DELETE', headers: authH });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('User deleted');
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-xl font-bold tracking-tight">Admin Panel Users</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Create staff accounts that can log in to this admin panel with restricted tab access.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> Add Admin User
        </button>
      </div>

      {/* User list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : users.length === 0 ? (
        <div className="border border-dashed rounded-xl p-10 text-center text-muted-foreground">
          <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No admin users yet</p>
          <p className="text-xs mt-1">Create a staff account and control which tabs they can access.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {users.map(u => {
            const tabs = u.permissions?.adminTabs || [];
            return (
              <div key={u.id} className="border rounded-xl p-4 flex items-start gap-4 bg-card">
                <div className="w-9 h-9 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{u.name}</span>
                    <span className="text-xs text-muted-foreground">{u.email}</span>
                    <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded px-1.5 py-0.5 font-semibold uppercase tracking-wider">Admin Staff</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tabs.length === 0
                      ? <span className="text-xs text-red-400">No tab access</span>
                      : tabs.map(t => {
                          const def = ADMIN_TABS.find(a => a.id === t);
                          return (
                            <span key={t} className="text-[10px] bg-slate-700/60 text-slate-300 rounded px-1.5 py-0.5">
                              {def?.label || t}
                            </span>
                          );
                        })
                    }
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(u)} className="p-1.5 rounded hover:bg-rose-600/20 text-slate-400 hover:text-rose-400">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog (inline slide-in) */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-card border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b flex items-center justify-between">
              <h4 className="font-bold text-base">{editing ? 'Edit Admin User' : 'New Admin User'}</h4>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground text-xl leading-none">×</button>
            </div>

            <div className="p-5 space-y-4">
              {/* Email */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Email / Username *</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
                  placeholder="staff@company.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  disabled={!!editing}
                />
                {editing && <p className="text-[10px] text-muted-foreground mt-1">Email cannot be changed after creation.</p>}
              </div>

              {/* Name */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Display Name</label>
                <input
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
                  placeholder="John Smith"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              {/* Password */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">
                  Password {editing ? '(leave blank to keep current)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    className="w-full border rounded-lg px-3 py-2 pr-9 text-sm bg-background"
                    placeholder={editing ? 'Leave blank to keep current' : 'Set a strong password'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  />
                  <button type="button" onClick={() => setShowPwd(p => !p)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Tab permissions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-muted-foreground">Admin Panel Tabs</label>
                  <div className="flex gap-2">
                    <button onClick={selectAll}  className="text-[10px] text-blue-400 hover:underline">All</button>
                    <button onClick={selectNone} className="text-[10px] text-slate-400 hover:underline">None</button>
                  </div>
                </div>

                {GROUPS.map(group => (
                  <div key={group} className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{group}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {ADMIN_TABS.filter(t => t.group === group).map(tab => {
                        const checked = form.adminTabs.includes(tab.id);
                        return (
                          <label
                            key={tab.id}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm transition-colors ${
                              checked
                                ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                                : 'border-border hover:bg-muted'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTab(tab.id)}
                              className="accent-blue-500 w-3.5 h-3.5"
                            />
                            {tab.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Note */}
              <p className="text-[10px] text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
                This user will log in at the same URL as the super admin. They will only see the tabs selected above. User management tabs are always restricted to the super admin only.
              </p>
            </div>

            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={closeForm} className="px-4 py-2 rounded-lg border text-sm hover:bg-muted">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
